// The kit's shared interaction recipes and motion hooks: focus ring, state layer, press
// sink, presence (stay mounted through an exit), list exits, FLIP moves, ticking numbers.
// docs/operations/design-standard.md, "Motion" and "Controls".

import { DependencyList, RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Keyboard focus only; a mouse click draws no ring. /70 keeps SwarmSystems' blue at 3:1 on every step. */
export const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70';
/** Keyboard focus drawn on a sibling face. An outline with a gap, because a ring on a gold face vanishes into it. */
export const PEER_FOCUS =
  'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary/70';
/** The hover and press state layer (index.css `.np-state`), laid over any fill as an image. */
export const STATE = 'np-state';
/** The state layer on a gold fill: the layer becomes the fill's own foreground. */
export const STATE_ON_PRIMARY = 'np-state np-state-on-primary';
/** A key sinks 1px under the finger. */
export const PRESS = 'active:translate-y-px';
/** What a control animates when its state changes: the press, the fill, the label. */
export const CONTROL_MOTION = 'transition-[transform,background-color,color,box-shadow,opacity,--np-layer] duration-quick ease-emphasized';
/** Grows a hit target on touch screens without growing what is drawn. The host must not clip. */
// relative anchors the grown hit area, unless the key is already positioned: a call site's
// absolute/fixed/sticky must win, and anchors the ::before just as well.
export const TOUCH = "[&:not(.absolute):not(.fixed):not(.sticky)]:relative [@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:-inset-1.5 [@media(pointer:coarse)]:before:content-['']";

// Inline transitions need the raw values. These mirror tailwind.config.js, which CRA will not
// let src/ import; change both together.
export const EASE = {
  emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  exit: 'cubic-bezier(0.3, 0, 1, 1)',
  spring: 'linear(0, 0.105, 0.319, 0.543, 0.73, 0.865, 0.952, 1.001, 1.023, 1.028, 1.026, 1.02, 1.013, 1.008, 1.004, 1.001, 1, 0.999, 0.999, 0.999, 0.999, 1, 1, 1, 1)',
  /** Charm moments only, on MS.spring. */
  springBouncy: 'linear(0, 0.112, 0.355, 0.624, 0.851, 1.009, 1.096, 1.126, 1.117, 1.088, 1.054, 1.023, 1.002, 0.989, 0.984, 0.985, 0.988, 0.992, 0.996, 0.999, 1.001, 1.002, 1.002, 1.002, 1)',
};
export const MS = { quick: 120, standard: 200, exit: 150, spring: 380 };

export const reducedMotion = (): boolean =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && !!window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;

const raf = (fn: () => void): number =>
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : window.setTimeout(fn, 16);
const unraf = (id: number) => {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
  else window.clearTimeout(id);
};

/**
 * Mount, paint closed, then open; on hide close and unmount once the exit has run.
 * `mounted` decides whether to render, `open` which side of the transition to draw.
 * Something already shown on first render starts open, with no entrance.
 */
export function usePresence(show: boolean, exitMs: number = MS.exit): { mounted: boolean; open: boolean } {
  const [mounted, setMounted] = useState(show);
  const [open, setOpen] = useState(show);

  useEffect(() => {
    if (show) {
      setMounted(true);
      // Two frames: the closed state has to paint once or nothing animates.
      let second = 0;
      const first = raf(() => { second = raf(() => setOpen(true)); });
      return () => { unraf(first); unraf(second); };
    }
    setOpen(false);
    if (reducedMotion()) {
      setMounted(false);
      return undefined;
    }
    const t = window.setTimeout(() => setMounted(false), exitMs);
    return () => window.clearTimeout(t);
  }, [show, exitMs]);

  return { mounted, open };
}

const HANDOFF = '[data-handoff]';
/** The keys in `el` marked to take focus handed over by a neighbour. */
const markedIn = (el: Element): HTMLElement[] =>
  [...(el.matches(HANDOFF) ? [el as HTMLElement] : []), ...Array.from(el.querySelectorAll<HTMLElement>(HANDOFF))];
/** A marked key that can take focus now: not hidden, not on its way out. */
const live = (k: HTMLElement) => k.tabIndex >= 0 && !k.closest('[aria-hidden="true"]');

/**
 * Offers handed-over focus to one neighbour: its first live key marked `data-handoff` (the
 * neighbour itself counts). `landed` when focus moved; `past` when every marked key is on its way
 * out, so the search steps over it; `stop` when it has nothing to take focus, which ends the search.
 */
export function offerFocus(n: Element): 'landed' | 'past' | 'stop' {
  const marked = markedIn(n);
  const landings = marked.filter(live);
  // A key that cannot take focus (display none, inert, a disabled fieldset) refuses
  // silently, so each one is checked before the search moves on.
  for (const key of landings) {
    key.focus({ preventScroll: true });
    if (document.activeElement === key) return 'landed';
  }
  return marked.length > 0 && landings.length === 0 ? 'past' : 'stop';
}

/**
 * A row or block that starts leaving while it holds focus hands focus to the nearest live row
 * after it, else before it, so a keyboard user is not dropped onto the page when the row they
 * were on goes. Focus lands only on a key marked `data-handoff` (a row's own link, never its
 * Delete). A neighbour that is leaving too (every marked key on its way out) is stepped over;
 * the first live row ends the search, so focus never lands rows away. With no marked key there,
 * focus stays and falls to the page when the row unmounts. Put the ref on the element whose
 * siblings are the other rows. Focus anywhere else is left alone.
 */
export function useFocusHandoff<T extends HTMLElement>(leaving: boolean): RefObject<T> {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!leaving || !el || !el.contains(document.activeElement)) return;
    for (const step of ['nextElementSibling', 'previousElementSibling'] as const) {
      for (let n = el[step]; n; n = n[step]) {
        const offered = offerFocus(n);
        if (offered === 'landed') return;
        if (offered === 'stop') break;
      }
    }
  }, [leaving]);
  return ref;
}

export interface Exiting<T> {
  key: string;
  item: T;
  /** Gone from the data, still drawn so it can run its exit. */
  leaving: boolean;
  /** Arrived after the first load, so it gets an entrance. */
  fresh: boolean;
}

/**
 * The items to draw: the current ones, plus ones the data just dropped, kept in their old place
 * (under whatever was drawn above them) and flagged `leaving` for `exitMs`, so they fade instead of
 * vanishing and the rest close the gap after (useFlip, or Reveal per item). `fresh` marks an
 * item that arrived after the first load. `null` means not loaded yet: nothing leaves.
 * `keep` false is a set change, not a removal (a page turned, a view switched): what the data drops
 * goes at once, and so does anything still on its way out.
 */
export function useExiting<T>(
  items: readonly T[] | null,
  keyOf: (item: T) => string,
  exitMs: number = MS.exit,
  keep: boolean = true,
): Exiting<T>[] {
  const prev = useRef<{ items: readonly T[] | null; keys: string[] }>({ items: null, keys: [] });
  // What was last drawn, leaving items included: a leaving item's place is read from here.
  const drawn = useRef<string[]>([]);
  const gone = useRef(new Map<string, { item: T; above: string[]; fresh: boolean; timer?: number }>());
  const fresh = useRef(new Map<string, boolean>());
  const primed = useRef(false);
  const [version, bump] = useState(0);

  // Diffed during render on purpose: an effect would draw one frame with the item already gone.
  if (items && prev.current.items !== items) {
    const keys = items.map(keyOf);
    const now = new Set(keys);
    const was = prev.current.keys;
    const old = prev.current.items || [];
    if (!keep) {
      gone.current.forEach((g) => window.clearTimeout(g.timer));
      gone.current.clear();
    }
    was.forEach((k, i) => {
      if (now.has(k) || gone.current.has(k)) return;
      if (!keep) {
        fresh.current.delete(k);
        return;
      }
      // Everything drawn above it, nearest first: if its neighbour leaves too, the next one up anchors it.
      const at = drawn.current.indexOf(k);
      const above = (at >= 0 ? drawn.current.slice(0, at) : was.slice(0, i)).reverse();
      gone.current.set(k, { item: old[i], above, fresh: fresh.current.get(k) ?? false });
      fresh.current.delete(k);
    });
    gone.current.forEach((g, k) => {
      if (now.has(k)) { window.clearTimeout(g.timer); gone.current.delete(k); }
    });
    keys.forEach((k) => { if (!fresh.current.has(k)) fresh.current.set(k, primed.current); });
    primed.current = true;
    prev.current = { items, keys };
  }

  useEffect(() => {
    gone.current.forEach((g, k) => {
      if (g.timer !== undefined) return;
      g.timer = window.setTimeout(() => { gone.current.delete(k); bump((n) => n + 1); }, exitMs);
    });
  });
  useEffect(() => {
    const map = gone.current;
    return () => map.forEach((g) => window.clearTimeout(g.timer));
  }, []);

  const shown = useMemo(() => {
    const keys = items ? prev.current.keys : [];
    const out: Exiting<T>[] = (items || []).map((item, i) => ({ key: keys[i], item, leaving: false, fresh: fresh.current.get(keys[i]) ?? false }));
    // In the order they were drawn, so a run of leaving items keeps its own order.
    const order = (k: string) => drawn.current.indexOf(k);
    Array.from(gone.current.entries())
      .sort(([a], [b]) => order(a) - order(b))
      .forEach(([key, g]) => {
        const anchor = g.above.find((k) => out.some((x) => x.key === k));
        const at = anchor === undefined ? -1 : out.findIndex((x) => x.key === anchor);
        out.splice(at + 1, 0, { key, item: g.item, leaving: true, fresh: g.fresh });
      });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, version]);

  useLayoutEffect(() => { drawn.current = shown.map((e) => e.key); });
  return shown;
}

/** The translate a running transition has reached, read off the computed matrix. */
const inFlight = (el: HTMLElement): [number, number] => {
  const t = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(el).transform : '';
  if (!t || t === 'none') return [0, 0];
  const n = t.match(/-?[\d.]+(?:e-?\d+)?/g)?.map(Number) || [];
  if (t.startsWith('matrix3d(') && n.length >= 16) return [n[13] || 0, n[14] || 0];
  if (t.startsWith('matrix(') && n.length >= 6) return [n[4] || 0, n[5] || 0];
  return [0, 0];
};

/**
 * FLIP for the children of `containerRef` marked `data-flip-key`: each keyed child that moved
 * since the last run glides from where it was on the spring; a key that is new fades and
 * settles in. The first run only records, so a page does not animate itself on load.
 * Removed keys simply drop out; give them an exit with usePresence if they need one.
 * Under reduced motion moves jump and new keys still fade.
 */
export function useFlip(containerRef: RefObject<HTMLElement>, deps: DependencyList): void {
  const last = useRef<Map<string, { x: number; y: number }>>(new Map());
  const primed = useRef(false);
  const cleanup = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-flip-key]'));
    const reduce = reducedMotion();

    // Read every in-flight offset, then clear them all, then measure: one write pass, one read
    // pass, so the browser lays out once rather than once per child.
    const flight = els.map(inFlight);
    els.forEach((el) => { el.style.transition = 'none'; el.style.transform = ''; el.style.opacity = ''; });
    const box = root.getBoundingClientRect();
    const now = els.map((el) => {
      const r = el.getBoundingClientRect();
      // Content coordinates, not visible ones: when the container itself scrolls, a scroll
      // between two updates must not read as every card having moved.
      return { x: r.left - box.left + root.scrollLeft, y: r.top - box.top + root.scrollTop };
    });

    const plays: Array<() => void> = [];
    const next = new Map<string, { x: number; y: number }>();
    els.forEach((el, i) => {
      const key = el.dataset.flipKey || '';
      next.set(key, now[i]);
      const was = last.current.get(key);
      if (was) {
        if (reduce) return;
        const dx = was.x + flight[i][0] - now[i].x;
        const dy = was.y + flight[i][1] - now[i].y;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        plays.push(() => {
          el.style.transition = `transform ${MS.spring}ms ${EASE.spring}`;
          el.style.transform = '';
        });
      } else if (primed.current) {
        el.style.opacity = '0';
        if (!reduce) el.style.transform = 'scale(0.96)';
        plays.push(() => {
          el.style.transition = `opacity ${MS.standard}ms ${EASE.emphasized}, transform ${MS.spring}ms ${EASE.spring}`;
          el.style.opacity = '';
          el.style.transform = '';
        });
      }
    });
    last.current = next;
    primed.current = true;
    // An inline 'none' left on a still child would also kill its own hover and colour transitions.
    els.forEach((el) => { if (!el.style.transform && !el.style.opacity) el.style.transition = ''; });

    if (plays.length) {
      // Commit the inverted positions before releasing them, or the browser skips straight to the end.
      void root.offsetHeight;
      plays.forEach((p) => p());
    }
    window.clearTimeout(cleanup.current);
    // Drop the inline transitions afterwards so they do not catch the element's own later changes.
    cleanup.current = window.setTimeout(() => {
      els.forEach((el) => { if (el.isConnected) el.style.transition = ''; });
    }, MS.spring + 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => () => window.clearTimeout(cleanup.current), []);
}

/**
 * A number that runs to its new value instead of jumping. Integers stay integers on the way.
 * Under reduced motion it is the value, at once.
 */
export function useTicker(value: number, ms: number = MS.spring): number {
  const [shown, setShown] = useState(value);
  const current = useRef(value);

  useEffect(() => {
    const from = current.current;
    if (from === value) return undefined;
    if (reducedMotion() || typeof requestAnimationFrame !== 'function' || ms <= 0) {
      current.current = value;
      setShown(value);
      return undefined;
    }
    // Whole by the TARGET: an interrupted tick starts from a fraction, and judging by that showed every decimal.
    const whole = Number.isInteger(value);
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
    let id = 0;
    const step = (t: number) => {
      const p = Math.min(1, Math.max(0, (t - start) / ms));
      const k = 1 - (1 - p) ** 3;
      const v = p >= 1 ? value : from + (value - from) * k;
      current.current = v;
      setShown(whole ? Math.round(v) : v);
      if (p < 1) id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [value, ms]);

  return shown;
}
